import React from "react";
import { cn } from "@/lib/utils";

/** Rail-node color for one timeline row. */
export type StepNodeState = "running" | "fresh" | "done" | "failed";

/** Entrance animation for rows appearing in a live turn's timeline. */
export const TIMELINE_ENTER_ANIMATION_CLASS =
  "duration-200 ease-out animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none";

const NODE_STATE_CLASS: Record<StepNodeState, string> = {
  running: "bg-warning animate-pulse motion-reduce:animate-none",
  fresh: "bg-success",
  done: "bg-muted-foreground/50",
  failed: "bg-danger",
};

interface TimelineRailItemProps {
  state: StepNodeState;
  isFirst: boolean;
  isLast: boolean;
  isThreadActive: boolean;
  children: React.ReactNode;
}

/**
 * Wraps one timeline row with its share of the vertical rail: the connecting
 * line segment and the state-colored node. A folded tool group draws a single
 * node here, exactly like any sibling step.
 */
export function TimelineRailItem({
  state,
  isFirst,
  isLast,
  isThreadActive,
  children,
}: TimelineRailItemProps) {
  return (
    <div
      className={cn(
        "relative",
        isThreadActive && TIMELINE_ENTER_ANIMATION_CLASS,
      )}
    >
      {!(isFirst && isLast) && (
        <span
          aria-hidden
          className={cn(
            "absolute -left-[17px] w-px bg-border",
            isFirst ? "top-[17px]" : "-top-0.5",
            isLast ? "h-[19px]" : "bottom-0",
          )}
        />
      )}
      <span
        aria-hidden
        className={cn(
          "absolute -left-5 top-[17px] h-[7px] w-[7px] -translate-y-1/2 rounded-full",
          NODE_STATE_CLASS[state],
        )}
      />
      {children}
    </div>
  );
}
